import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // Not authenticated (401) is an expected state, not an error to surface.
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Server-side logout is best-effort; the local session is cleared regardless.
    }
    setUser(false);
  };

  const forgotPassword = useCallback((email) => api.post("/auth/forgot-password", { email }), []);
  const resetPassword = useCallback((token, password) => api.post("/auth/reset-password", { token, password }), []);
  const verifyEmail = useCallback((token) => api.post("/auth/verify-email", { token }), []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.patch("/auth/me", payload);
    setUser(data);
    return data;
  }, []);

  const deleteAccount = useCallback(async () => {
    await api.delete("/auth/account");
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthed: !!user,
      login, register, logout, forgotPassword, resetPassword, verifyEmail,
      updateProfile, deleteAccount, refresh: loadUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
